import type { MouseEvent } from 'react';
import React, { useEffect } from 'react';
import { FavouriteButton, PrimaryLabel } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import Image from 'next/image';

import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import {
  PRODUCT_IMAGE_TABPANEL_CLASS,
  PRODUCT_IMAGES_TAB_ID_PREFIX,
  PRODUCT_IMAGES_TABPANEL_CONTAINER_ID,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
} from 'src/components/organisms/ProductImagesCarousel/constants/elementIds';
import { useProductImagesCarousel } from 'src/components/organisms/ProductImagesCarousel/hooks/useProductImagesCarousel';
import * as styles from 'src/components/organisms/ProductImagesCarousel/styles/ProductImages.styles';
import type {
  ImagesLocation,
  NextOrPreviousPayload,
  OpenModalPayload,
  ProductImagesState,
  SetCurrentImagePayload,
} from 'src/components/organisms/ProductImagesCarousel/types';
import type { CatServiceAPIProduct } from 'src/services/cat-service-api/types/CatServiceAPIProduct';
import { PRODUCT_IMAGES_SECTION_LABEL_SINGLE } from '../constants/copy';
import { ImageNumberLabel } from './ImageNumberLabel/ImageNumberLabel';
import { NextButton, PreviousButton } from './NavigationButtons/NavigationButtons';

const handleImageClick = (
  e: MouseEvent<HTMLAnchorElement>,
  onOpenModal: MultipleProductImagesProps['onOpenModal'],
) => {
  e.preventDefault();
  e.stopPropagation();
  onOpenModal({ location: 'Main image' });
};

export interface MultipleProductImagesProps {
  currentImage: ProductImagesState['currentImage'];
  location: ImagesLocation;
  navigatedBy: ProductImagesState['navigatedBy'];
  onBeforeSwipe?: () => void;
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onOpenModal: (payload: OpenModalPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
  onUpdateFavourite: () => void;
  product: CatServiceAPIProduct;
  productIsFavourited: boolean;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

export const MainProductImage = ({
  currentImage,
  location,
  navigatedBy,
  onBeforeSwipe,
  onNextImage,
  onOpenModal,
  onPrevImage,
  onUpdateFavourite,
  product: { availability, available, images, isNew, purchasable },
  productIsFavourited,
  setCurrentImage,
}: MultipleProductImagesProps) => {
  const [imageFocus, setImageFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);
  const [loadedImages, setImageLoaded] = React.useState<Record<number, boolean>>({});
  const totalImages = images.length;

  useEffect(() => {
    if (currentImage > 0) {
      // User has navigated past the first image
      setHasInteractedWithCarousel(true);
    }
  }, [currentImage]);

  const [carouselContainerRef, carouselItemRefs] = useProductImagesCarousel(
    (entry) => {
      onBeforeSwipe?.();
      const imageIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentImage != imageIndex) {
        setCurrentImage({ imageIndex, navigationType: 'swipe', location });
      }
    },
    currentImage,
    navigatedBy,
    images.length,
  );

  return (
    <PreventSwipeNavigation aria-live="polite" css={[styles.imagesWrapper]}>
      <section aria-label={PRODUCT_IMAGES_SECTION_LABEL_SINGLE} css={[styles.carouselContainer]}>
        <div
          css={[styles.mainImageWrapperWithThumbnails]}
          data-image-focus={imageFocus}
          id={PRODUCT_IMAGES_TABPANEL_CONTAINER_ID}
          ref={carouselContainerRef}
        >
          {images.map(({ href: src, title }, i) => (
            <div
              aria-hidden={currentImage !== i}
              aria-labelledby={`${PRODUCT_IMAGES_TAB_ID_PREFIX}${i}`}
              className={PRODUCT_IMAGE_TABPANEL_CLASS}
              css={styles.carouselSlide}
              data-item-index={i}
              id={`${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
              key={src}
              ref={(node) => {
                carouselItemRefs.current[i] = node as HTMLElement;
              }}
              role="tabpanel"
            >
              <a
                css={styles.mainImageLink}
                href={getProductImageURL(src, 'original')}
                onBlur={() => setImageFocus(false)}
                onClick={(e) => handleImageClick(e, onOpenModal)}
                onFocus={(e) => setImageFocus(isKeyboardTriggeredEvent(e))}
                tabIndex={currentImage === i ? undefined : -1}
              >
                <Image
                  alt={`${title}, ${i + 1} of ${images.length}`}
                  css={styles.mainImage}
                  fill={true}
                  onLoad={() => setImageLoaded({ ...loadedImages, [i]: true })}
                  priority={i === 0}
                  src={getProductImageURL(
                    src,
                    i === 0 || hasInteractedWithCarousel ? 'original' : 'normal',
                  )}
                  unoptimized
                />
              </a>
            </div>
          ))}
          <PrimaryLabel
            className="product-image__label"
            css={styles.productLabel}
            hasStock={purchasable}
            isNew={isNew}
            unavailable={!available && availability === 'suspended'}
          />
        </div>
        {totalImages > 1 && (
          <>
            <PreviousButton onClick={() => onPrevImage({ location, totalImages })} />
            <NextButton onClick={() => onNextImage({ location, totalImages })} />
            <ImageNumberLabel currentImage={currentImage + 1} totalImages={totalImages} />
          </>
        )}
        <div css={styles.favouriteButtonWrapper}>
          <FavouriteButton
            active={productIsFavourited}
            css={styles.favouriteButton}
            onClick={onUpdateFavourite}
            size="large"
          />
        </div>
      </section>
    </PreventSwipeNavigation>
  );
};
